/* eslint-disable no-custom-animation/no-custom-animation */

import type { UnstyledButtonProps } from '@mantine/core';
import {
	Anchor,
	createPolymorphicComponent,
	createStyles,
	Loader,
	UnstyledButton,
} from '@mantine/core';
import { getStylesRef, rem } from '@mantine/styles';
import { typography } from '@repo/theme/primitives';
import type { ColorNames } from '@repo/theme/utils';
import { modifyOpacityOfRgbaColor } from '@repo/theme/utils';
import { forwardRef, memo, useCallback } from 'react';
import { Icon, type IconNames } from '../Icon';
export type ButtonVariants = 'default' | 'primary' | 'tertiary' | 'plain';
export type ButtonTones = 'default' | 'critical';
export type ButtonSizes = 'sm' | 'md' | 'lg' | '2xl';

export type IconAnimation =
	| 'spin'
	| 'spinCounterClockwise'
	| 'rotate90'
	| 'rotate180'
	| 'rotateCounterClockwise90'
	| 'rotateCounterClockwise180';

export interface ButtonClassNames {
	root?: string;
	inner?: string;
	label?: string;
	leftIcon?: string;
	rightIcon?: string;
}

export interface ButtonProps
	extends Omit<
		UnstyledButtonProps,
		'unstyled' | 'variant' | 'children' | 'classNames'
	> {
	/** Controls button appearance  */
	variant?: ButtonVariants;
	/** Controls button appearance  */
	tone?: ButtonTones;
	/** Predefined button size */
	size?: ButtonSizes;
	/** Button type attribute */
	type?: 'submit' | 'button' | 'reset';
	/** Adds icon before button label  */
	leftIconName?: IconNames;
	/** Animation for the left icon */
	leftIconAnimation?: IconAnimation;
	/** Adds icon after button label  */
	rightIconName?: IconNames;
	/** Animation for the right icon */
	rightIconAnimation?: IconAnimation;
	/** Makes the icon in the button a specific color. Use `tone` when possible.  */
	iconColor?: ColorNames;
	/** Makes the left icon a filled icon */
	leftIconFilled?: boolean;
	/** Button label */
	children: React.ReactNode;
	/** Callback for when it's clicked */
	onClick?: (event: React.MouseEvent<HTMLButtonElement>) => void;
	/** Indicate loading state */
	loading?: boolean;
	/** Disabled state */
	disabled?: boolean;
	/** Highlight the button visually */
	highlight?: boolean;
	/** Class names for different parts of the button */
	classNames?: ButtonClassNames;
	id?: string;
}

interface ButtonStyleProps {
	loading: boolean;
	disabled: boolean;
	size: ButtonSizes;
	variant: ButtonVariants;
	tone: ButtonTones;
	iconColor?: ColorNames;
	iconFillColor?: ColorNames;
	hasLeftIcon: boolean;
	hasRightIcon: boolean;
}

const useStyles = createStyles(
	(
		theme,
		{
			size,
			loading,
			disabled,
			variant,
			tone,
			iconColor: iconColorProp,
			hasLeftIcon,
			hasRightIcon,
		}: ButtonStyleProps
	) => {
		let [height, paddingX] = [theme.other.space[7], theme.other.space[3]];
		let labelColor: ColorNames = 'text/primary/default';
		let fillColor: ColorNames = 'fill/primary/default';
		let iconColor: ColorNames = 'icon/primary/default';
		let hoverFillColor: ColorNames = 'fill/primary/hover';
		let hoverLabelColor: ColorNames = 'text/primary/default';
		let activeFillColor: ColorNames = 'fill/primary/active';
		let activeLabelColor: ColorNames = 'text/primary/default';
		let disabledFillColor: ColorNames = 'fill/primary/disabled';
		let disabledTextColor: ColorNames = 'text/primary/disabled';
		let highlight = '';
		let highlightOnActive = '';

		if (size === '2xl') {
			[height] = [theme.other.space[10]];
		} else if (size === 'lg') {
			[height] = [theme.other.space[8]];
		} else if (size === 'sm') {
			[height, paddingX] = [theme.other.space[6], theme.other.space[2]];
		}
		if (variant === 'default') {
			fillColor = 'fill/primary/default';
			iconColor = 'icon/primary/default';
			labelColor = 'text/primary/default';
			hoverFillColor = 'fill/primary/hover';
			activeFillColor = 'fill/primary/hover';
			disabledFillColor = 'fill/primary/disabled';
			disabledTextColor = 'text/primary/disabled';
			highlight = `0px 1px 0px 0px ${theme.other.getColor('shadow/button-default')} inset,
        1px 0px 0px 0px ${theme.other.getColor('shadow/button-default')} inset,
        -1px 0px 0px 0px ${theme.other.getColor('shadow/button-default')} inset,
        0px -1px 0px 0px ${theme.other.getColor('shadow/button-default-bottom')} inset`;
			highlightOnActive = `0px 2px 1px 0px ${modifyOpacityOfRgbaColor(theme.other.getColor('fill/transparent-secondary/default'), 0.12)} inset,
1px 1px 1px 0px ${modifyOpacityOfRgbaColor(theme.other.getColor('fill/transparent-secondary/default'), 0.12)} inset,
-1px 0px 1px 0px ${modifyOpacityOfRgbaColor(theme.other.getColor('fill/transparent-secondary/default'), 0.12)} inset`;
			if (tone === 'default') {
				labelColor = 'text/primary/default';
			} else if (tone === 'critical') {
				labelColor = 'text/critical/default';
				hoverLabelColor = 'text/critical/default';
				activeLabelColor = 'text/critical/default';
				iconColor = 'text/critical/default';
			}
			if (loading) {
				fillColor = 'fill/primary/disabled';
			}
		} else if (variant === 'primary') {
			fillColor = 'fill/brand/default';
			iconColor = 'text/brand-on-fill/default';
			labelColor = 'text/brand-on-fill/default';
			hoverLabelColor = 'text/brand-on-fill/hover';
			activeLabelColor = 'text/brand-on-fill/active';
			hoverFillColor = 'fill/brand/hover';
			activeFillColor = 'fill/brand/active';
			disabledFillColor = 'fill/brand/disabled';
			disabledTextColor = 'text/brand-on-fill/disabled';
			highlight = `0px 2px 0px 0px ${theme.other.getColor('shadow/reflection')} inset,
2px 0px 0px 0px ${theme.other.getColor('shadow/reflection')} inset,
-2px 0px 0px 0px ${theme.other.getColor('shadow/reflection')} inset,
0px -1px 0px 1px ${theme.other.getColor('shadow/button-shadow')} inset,
0px 1px 0px 0px ${theme.other.getColor('shadow/button-shadow')} inset`;
			highlightOnActive = `0px 3px 0px 0px ${theme.other.getColor('shadow/button-shadow')} inset`;
			if (tone === 'default') {
				labelColor = 'text/brand-on-fill/default';
			} else if (tone === 'critical') {
				fillColor = 'fill/critical/default';
				hoverFillColor = 'fill/critical/hover';
				activeFillColor = 'fill/critical/active';
				labelColor = `${theme.colorScheme !== 'dark' ? 'text/critical-on-fill/default' : 'text/critical/default'}`;
				hoverLabelColor = `${theme.colorScheme !== 'dark' ? 'text/critical-on-fill/hover' : 'text/critical/hover'}`;
				activeLabelColor = `${theme.colorScheme !== 'dark' ? 'text/critical-on-fill/active' : 'text/critical/active'}`;
				iconColor = `${theme.colorScheme !== 'dark' ? 'text/critical-on-fill/default' : 'text/critical/default'}`;
				highlight =
					theme.colorScheme !== 'dark'
						? '0px 1px 0px 0px rgba(255, 255, 255, 0.48) inset, -1px 0px 0px 0px rgba(255, 255, 255, 0.20) inset, 1px 0px 0px 0px rgba(255, 255, 255, 0.20) inset, 0px -1.5px 0px 0px rgba(0, 0, 0, 0.25) inset'
						: `0px 1px 0px 0px rgba(255, 255, 255, 0.20) inset, -1px 0px 0px 0px rgba(255, 255, 255, 0.10) inset, 1px 0px 0px 0px rgba(255, 255, 255, 0.10) inset, 0px -1.5px 0px 0px ${theme.other.getColor('shadow/button-shadow')} inset`;
				highlightOnActive = `0px 3px 0px 0px ${theme.other.getColor(
					theme.colorScheme !== 'dark'
						? 'fill/critical/active'
						: 'shadow/button-shadow'
				)} inset`;
			}
			if (loading) {
				fillColor = 'fill/brand/disabled';
			}
		} else if (variant === 'tertiary') {
			fillColor = 'fill/transparent/default';
			iconColor = 'icon/primary/default';
			labelColor = 'text/primary/default';
			hoverFillColor = 'fill/transparent/hover';
			activeFillColor = 'fill/transparent/active';
			disabledFillColor = 'fill/transparent/default';
			disabledTextColor = 'text/primary/disabled';
		}

		if (iconColorProp) {
			// If we get a color from a prop we use it directly. Using `tone` is preferable since it will have active and hover states.
			iconColor = iconColorProp;
		}

		return {
			container: {
				display: 'inline-block',
				position: 'relative',
				height,
				borderRadius: theme.other.space[2],
				fontSize: size === 'lg' ? typography.text.sm : typography.text.xs,
				fontWeight: typography.weight.semibold,
				lineHeight:
					size === 'lg'
						? typography.lineHeight.text.sm
						: typography.lineHeight.text.xs,

				backgroundColor: theme.other.getColor(fillColor),
				color: theme.other.getColor(labelColor),
				boxShadow: loading || disabled ? 'none' : highlight,
				transition: 'box-shadow 0.075s ease-in-out 0s',
				'&:hover': {
					backgroundColor: theme.other.getColor(hoverFillColor),
					color: theme.other.getColor(hoverLabelColor),
				},
				'&:active': {
					backgroundColor: theme.other.getColor(activeFillColor),
					color: theme.other.getColor(activeLabelColor),
					boxShadow: highlightOnActive,
					[`.${getStylesRef('inner')}`]: {
						transform: variant !== 'tertiary' ? 'translateY(1px)' : undefined,
					},
				},
				'&[data-active=true]': {
					backgroundColor: theme.other.getColor(activeFillColor),
					color: theme.other.getColor(activeLabelColor),
					boxShadow: highlightOnActive,
					[`.${getStylesRef('inner')}`]: {
						transform: variant !== 'tertiary' ? 'translateY(1px)' : undefined,
					},
					'&:hover': {
						backgroundColor: theme.other.getColor(hoverFillColor),
						color: theme.other.getColor(hoverLabelColor),
					},
				},
				'&:focus': {
					outline: `solid ${theme.other.getColor(
						'border/emphasis/default'
					)} 2px`,
					outlineOffset: rem(theme.other.space[0.25]),
				},
				'&:disabled': {
					backgroundColor: theme.other.getColor(disabledFillColor),
					color: theme.other.getColor(disabledTextColor),
					cursor: 'not-allowed',
					pointerEvents: 'none',
				},
			},
			icon: {
				color: disabled
					? theme.other.getColor(disabledTextColor)
					: theme.other.getColor(iconColor),
				stroke: disabled
					? theme.other.getColor(disabledTextColor)
					: theme.other.getColor(iconColor),
			},
			shine: {
				position: 'absolute',
				width: '100%',
				height: '100%',
				top: 0,
				left: 0,

				background:
					variant === 'primary' && theme.colorScheme !== 'dark'
						? 'linear-gradient(180deg, rgba(255, 255, 255, 0.04) 87%, rgba(255, 255, 255, 0.16) 100%)'
						: undefined,
			},
			inner: {
				ref: getStylesRef('inner'),
				display: 'flex',
				alignItems: 'center',
				justifyContent: 'center',
				gap: theme.other.space[0.5],
				overflow: 'visible',
				opacity: loading ? 0 : 1,
				height: '100%',
				paddingLeft: hasLeftIcon ? `${rem(paddingX - 6)}` : `${rem(paddingX)}`,
				paddingRight: hasRightIcon
					? `${rem(paddingX - 6)}`
					: `${rem(paddingX)}`,
				color: disabled ? theme.other.getColor(disabledTextColor) : 'inherit',
				transition: 'transform 0.075s ease-in-out 0s',
			},
			loader: {
				position: 'absolute',
				left: '50%',
				top: `calc(50% - ${theme.other.space[2]}px)`,
				height: '100%',
				transform: 'translateX(-50%)',
			},
			label: {
				whiteSpace: 'nowrap',
				height: '100%',
				overflow: 'hidden',
				display: 'flex',
				alignItems: 'center',
			},
			// ANIMATIONS FOR ICONS
			iconAnimation: {
				'&.spin': {
					animation: 'spin 2s linear infinite',
				},
				'&.spinCounterClockwise': {
					animation: 'spinCounterClockwise 2s linear infinite',
				},
				'&.rotate90': {
					transform: 'rotate(90deg)',
				},
				'&.rotate180': {
					transform: 'rotate(180deg)',
				},
				'&.rotateCounterClockwise90': {
					transform: 'rotate(-90deg)',
				},
				'&.rotateCounterClockwise180': {
					transform: 'rotate(-180deg)',
				},
				'@keyframes spin': {
					from: {
						transform: 'rotate(0deg)',
					},
					to: {
						transform: 'rotate(360deg)',
					},
				},
				'@keyframes spinCounterClockwise': {
					from: {
						transform: 'rotate(0deg)',
					},
					to: {
						transform: 'rotate(-360deg)',
					},
				},
			},
			leftIconClasses: {
				// The classes will be applied when the animation prop exists
				'&.spin': {
					animation: 'spin 2s linear infinite',
				},
				'&.spinCounterClockwise': {
					animation: 'spinCounterClockwise 2s linear infinite',
				},
				'&.rotate90': {
					transform: 'rotate(90deg)',
				},
				'&.rotate180': {
					transform: 'rotate(180deg)',
				},
				'&.rotateCounterClockwise90': {
					transform: 'rotate(-90deg)',
				},
				'&.rotateCounterClockwise180': {
					transform: 'rotate(-180deg)',
				},
			},
			rightIconClasses: {
				// The classes will be applied when the animation prop exists
				'&.spin': {
					animation: 'spin 2s linear infinite',
				},
				'&.spinCounterClockwise': {
					animation: 'spinCounterClockwise 2s linear infinite',
				},
				'&.rotate90': {
					transform: 'rotate(90deg)',
				},
				'&.rotate180': {
					transform: 'rotate(180deg)',
				},
				'&.rotateCounterClockwise90': {
					transform: 'rotate(-90deg)',
				},
				'&.rotateCounterClockwise180': {
					transform: 'rotate(-180deg)',
				},
			},
		};
	}
);

const Button = createPolymorphicComponent<'button', ButtonProps>(
	memo(
		forwardRef<HTMLButtonElement, ButtonProps>(
			(
				{
					id,
					children,
					onClick,
					variant = 'default',
					tone = 'default',
					leftIconName,
					leftIconAnimation,
					iconColor,
					leftIconFilled,
					rightIconName,
					rightIconAnimation,
					size = 'md',
					loading = false,
					disabled = false,
					highlight = false,
					className,
					classNames,
					styles,
					...other
				},
				ref
			) => {
				const { classes, cx, theme } = useStyles({
					size,
					loading,
					disabled,
					variant,
					tone,
					iconColor,
					hasLeftIcon: !!leftIconName,
					hasRightIcon: !!rightIconName,
				});

				const handleClick = useCallback(
					(event: React.MouseEvent<HTMLButtonElement>) => {
						if (onClick) {
							onClick(event);
						}
					},
					[onClick]
				);

				if (variant === 'plain') {
					return (
						<Anchor
							component="button"
							type="button"
							disabled={disabled || loading}
							data-active={highlight || undefined}
							onClick={handleClick}
							{...other}
						>
							{children}
						</Anchor>
					);
				}

				return (
					<UnstyledButton
						id={id}
						className={cx(classes.container, className, classNames?.root)}
						styles={styles}
						disabled={disabled || loading}
						data-active={highlight || undefined}
						onClick={handleClick}
						ref={ref}
						{...other}
					>
						{variant === 'primary' ? (
							<div className={classes.shine} role="none" />
						) : null}
						{loading === true && (
							<span className={classes.loader}>
								<Loader
									size="xs"
									color={theme.other.getColor(
										variant === 'primary'
											? 'text/brand-on-fill/disabled'
											: 'icon/primary/disabled'
									)}
								/>
							</span>
						)}
						<span
							className={cx(
								classes.inner,
								'mantine-Button-inner',
								classNames?.inner
							)}
						>
							{leftIconName && (
								<Icon
									className={cx(
										classes.icon,
										classes.leftIconClasses,
										leftIconAnimation,
										classNames?.leftIcon
									)}
									name={leftIconName}
									color={leftIconFilled ? 'fill/primary/default' : iconColor}
									fillColor={leftIconFilled ? iconColor : undefined}
								/>
							)}

							<span
								className={cx(
									classes.label,
									'mantine-Button-label',
									classNames?.label
								)}
							>
								{children}
							</span>
							{rightIconName && (
								<Icon
									name={rightIconName}
									className={cx(
										classes.icon,
										classes.rightIconClasses,
										rightIconAnimation,
										classNames?.rightIcon
									)}
								/>
							)}
						</span>
					</UnstyledButton>
				);
			}
		)
	)
);

Button.displayName = 'Button';

export default Button;
